import { type ReactNode, useCallback, useState } from "react";

import { type AppName, AppSelector } from "#components/AppSelector";
import { Button } from "#components/Button";
import { ArrowLineRightIcon, StackletLogo } from "#icons";
import { cn } from "#utils";

import { type NavItem, NavItemList, type SidebarVariant } from "./NavItemList";
import { SidebarPopout } from "./SidebarPopout";

interface AppOption {
  label: string;
  href: string;
  isBeta: boolean;
}

export interface SidebarProps {
  appSelectorOptions: AppOption[];
  selectedApp: AppName;
  pathname: string;
  SignInMenu?: ReactNode;
  ProviderSelector?: ReactNode;
  navItems: NavItem[];
  collapsed?: boolean;
  /** Whether nav sections can be collapsed. When false (default), sections are always expanded with no toggle trigger. Overridable per section via `NavItem.collapsible`. */
  collapsible?: boolean;
  /** Visual style of the navigation, see {@link SidebarVariant}. */
  variant?: SidebarVariant;
  navigate: (to: string) => void;
  handleCollapsed?: () => void;
  className?: string;
}

export function Sidebar({
  appSelectorOptions,
  selectedApp,
  ProviderSelector,
  SignInMenu,
  pathname,
  navItems,
  collapsed = false,
  collapsible = false,
  variant = "default",
  handleCollapsed,
  navigate,
  className,
}: SidebarProps) {
  const [openPopoutId, setOpenPopoutId] = useState<string | null>(null);

  const handlePopoutToggle = (id: string) => {
    setOpenPopoutId((prev) => (prev === id ? null : id));
  };

  const handleNavigate = useCallback(
    (to: string) => {
      setOpenPopoutId(null);
      navigate(to);
    },
    [navigate],
  );

  const activePopoutItem = openPopoutId
    ? navItems.find((item) => item.id === openPopoutId && item.popoutContent !== undefined)
    : null;

  return (
    <div className="relative flex h-screen">
      <aside
        className={cn(
          "text-primary z-10 flex h-screen w-[13.625rem] flex-col items-center border-overlay-medium bg-background-secondary shadow backdrop-blur",
          collapsed ? "w-[4.5rem]" : "",
          className,
        )}
      >
        <div
          className={cn(
            "flex w-full items-center justify-between gap-x-sm px-xl py-lg",
            collapsed ? "flex-col gap-md" : "",
          )}
        >
          <a className="flex justify-center" href="/">
            <StackletLogo className="text-brand-primary" size={28} />
          </a>

          <AppSelector
            appOptions={appSelectorOptions}
            collapsed={collapsed}
            selected={selectedApp}
          />
        </div>
        {collapsed ? (
          <Button
            className="size-fit mb-lg rounded-md border border-neutral-white px-px py-px text-interactive-glass-button-enabled-foreground hover:bg-neutral-white"
            onPress={handleCollapsed ? () => handleCollapsed() : () => null}
            variant="plain"
          >
            <ArrowLineRightIcon className="size-xxl" />
          </Button>
        ) : null}
        {/* Margins match the header row's px-xl so the divider spans exactly
            the logo-to-app-selector width, in both expanded and rail mode. */}
        <hr className="mx-xl mb-lg h-1 self-stretch border-outline-decorative" />

        {!collapsed && ProviderSelector && (
          <div className="mb-lg flex justify-center px-xl">{ProviderSelector}</div>
        )}

        <div
          className={cn(
            "flex w-full flex-col overflow-x-hidden overflow-y-scroll",
            // Grouped sections are always separated by a `space-md` gap.
            variant === "grouped" ? "gap-md" : "",
          )}
          style={{ scrollbarWidth: "none" }}
        >
          {navItems.map((navitem, index) => {
            // In the default variant, items render in visual groups separated
            // by a `space-md` gap. A group boundary is declared explicitly via
            // `startsGroup` on the first item of a group; the gap is
            // suppressed for the first item.
            const startsNewBlock =
              variant === "default" && index > 0 && Boolean(navitem.startsGroup);

            return (
              <div className={startsNewBlock ? "mt-md" : undefined} key={navitem.id}>
                <NavItemList
                  Icon={navitem.Icon}
                  collapsed={collapsed}
                  collapsible={collapsible}
                  data={navitem}
                  navigate={handleNavigate}
                  onPopoutToggle={handlePopoutToggle}
                  pathname={pathname}
                  variant={variant}
                />
              </div>
            );
          })}
        </div>
        <div className="mb-xs mt-auto flex items-center justify-center pt-md">
          {SignInMenu ? SignInMenu : null}
        </div>
      </aside>

      <SidebarPopout isOpen={!!activePopoutItem}>
        {activePopoutItem?.popoutContent ?? null}
      </SidebarPopout>
    </div>
  );
}
